import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import type { ConsentState, GcmConsentApi } from '#nuxt-scripts/types'
import {
  ANALYTICS_CONSENT_STORAGE_KEY,
  GA_MEASUREMENT_ID,
  GTM_CONTAINER_ID,
  HOTJAR_SCRIPT_VERSION,
  HOTJAR_SITE_ID,
  MATOMO_TAG_MANAGER_URL,
} from '~/constants/analytics'
import type {
  AnalyticsConsentComposableReturn,
  AnalyticsConsentStatus,
  GoogleAnalyticsScriptContext,
} from '~/types/AnalyticsConsent'
import { enrichPostHogProfil, trackCookieConsent } from '~/composables/useTracking'

type ScriptConsentTrigger = ReturnType<typeof useScriptTriggerConsent>

const DENIED_GOOGLE_CONSENT: ConsentState = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
}

const GRANTED_GOOGLE_CONSENT: ConsentState = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
}

let consentGatedScriptsRegistered: boolean = false

let matomoTagManagerRegistered: boolean = false

let consentTrigger: ScriptConsentTrigger | null = null

let googleConsent: GcmConsentApi | null = null

let gtmConsent: GcmConsentApi | null = null

/**
 * Charge Matomo Tag Manager dès l'initialisation client (exemption CNIL, sans bandeau).
 * @returns {void}
 */
function registerMatomoTagManager(): void {
  if (matomoTagManagerRegistered || import.meta.server) {
    return
  }

  matomoTagManagerRegistered = true
  pushMatomoStartEvent()

  useScript({
    src: MATOMO_TAG_MANAGER_URL,
    async: true,
  })
}

/**
 * Enregistre GA, GTM et Hotjar avec chargement bloqué par le consentement.
 * @returns {void}
 */
function registerConsentGatedScripts(): void {
  if (consentGatedScriptsRegistered || import.meta.server) {
    return
  }

  consentGatedScriptsRegistered = true

  consentTrigger = useScriptTriggerConsent()

  const googleAnalytics: GoogleAnalyticsScriptContext = useScriptGoogleAnalytics({
    id: GA_MEASUREMENT_ID,
    defaultConsent: DENIED_GOOGLE_CONSENT,
    scriptOptions: {
      trigger: consentTrigger,
    },
  })

  googleConsent = googleAnalytics.consent ?? null

  const googleTagManager = useScriptGoogleTagManager({
    id: GTM_CONTAINER_ID,
    defaultConsent: DENIED_GOOGLE_CONSENT,
    scriptOptions: {
      bundle: false,
      trigger: consentTrigger,
    },
  })

  gtmConsent = googleTagManager.consent ?? null

  useScriptHotjar({
    id: HOTJAR_SITE_ID,
    sv: HOTJAR_SCRIPT_VERSION,
    scriptOptions: {
      trigger: consentTrigger,
    },
  })
}

/**
 * Initialise la file Matomo (`_mtm`) avant le chargement du container.
 * @returns {void}
 */
function pushMatomoStartEvent(): void {
  const queue: Array<Record<string, string | number>> = window._mtm ?? []
  window._mtm = queue
  queue.push({
    'mtm.startTime': new Date().getTime(),
    event: 'mtm.Start',
  })
}

/**
 * Passe Google Analytics en mode consentement accordé (Consent Mode v2).
 * @returns {void}
 */
function grantGoogleAnalyticsConsent(): void {
  googleConsent?.update(GRANTED_GOOGLE_CONSENT)
}

/**
 * Passe Google Tag Manager en mode consentement accordé (Consent Mode v2).
 * @returns {void}
 */
function grantGoogleTagManagerConsent(): void {
  gtmConsent?.update(GRANTED_GOOGLE_CONSENT)
}

/**
 * Active la capture PostHog (opt-in) une fois le consentement accordé.
 * PostHog est initialisé en `opt_out_capturing_by_default` : rien n'est capturé
 * tant que cette fonction n'a pas été appelée.
 * @returns {void}
 */
function grantPostHogConsent(): void {
  usePostHog()?.opt_in_capturing()
}

/**
 * Désactive la capture PostHog (opt-out) lorsque le consentement est refusé.
 * @returns {void}
 */
function revokePostHogConsent(): void {
  usePostHog()?.opt_out_capturing()
}

/**
 * Débloque le gate Nuxt Scripts et applique les consentements vendors.
 * @returns {void}
 */
function activateAnalyticsScripts(): void {
  grantGoogleAnalyticsConsent()
  grantGoogleTagManagerConsent()
  grantPostHogConsent()
  const userStore = useUserStore()
  if (userStore.userId) void enrichPostHogProfil(userStore.userId)
  consentTrigger?.accept()
}

/**
 * Gère le bandeau cookies et le chargement conditionnel des tags analytics (Nuxt Scripts).
 * @returns {AnalyticsConsentComposableReturn} État du consentement et actions accept / reject.
 */
export function useAnalyticsConsent(): AnalyticsConsentComposableReturn {
  registerMatomoTagManager()
  registerConsentGatedScripts()

  const status = useState<AnalyticsConsentStatus>('analytics-consent-status', () => null)
  const hydrated = useState<boolean>('analytics-consent-hydrated', () => false)

  const showBanner: ComputedRef<boolean> = computed(() => hydrated.value && status.value === null)

  /**
   * Lit `localStorage` et réactive les scripts si le consentement avait été accepté.
   * @returns {void}
   */
  function hydrateFromStorage(): void {
    if (hydrated.value || import.meta.server) {
      return
    }

    hydrated.value = true

    const stored: string | null = localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY)

    if (stored === 'accepted') {
      status.value = 'accepted'
      activateAnalyticsScripts()
      return
    }

    if (stored === 'rejected') {
      status.value = 'rejected'
    }
  }

  /**
   * Enregistre l'acceptation et charge les scripts analytics.
   * @returns {void}
   */
  function accept(): void {
    status.value = 'accepted'
    localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, 'accepted')
    activateAnalyticsScripts()
    trackCookieConsent('accepted')
  }

  /**
   * Enregistre le refus et révoque le gate de chargement.
   * @returns {void}
   */
  function reject(): void {
    status.value = 'rejected'
    localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, 'rejected')
    trackCookieConsent('rejected')
    revokePostHogConsent()
    consentTrigger?.revoke()
  }

  return {
    status,
    showBanner,
    hydrateFromStorage,
    accept,
    reject,
  }
}
